import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate} from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useSocket } from '../../contexts/SocketContext';
import Modal from '../../components/Modal';
import { Search, ArrowLeft, ChevronDown, ChevronRight, Trash2, MailX, AlertTriangle, Camera } from 'lucide-react';
import api from '../../utils/api';
import { beijingToday } from '../../utils/datetime';
import AdminNotificationBell from '../../components/AdminNotificationBell';

export default function DispatchViewPage() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { showToast } = useToast();
  const { subscribe } = useSocket();
  const [groups, setGroups] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  // 折叠状态：默认展开今天
  const [expanded, setExpanded] = useState({});
  // 勾选的日期（单选或多选后批量删除）
  const [selected, setSelected] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const today = beijingToday();
  const [imageCounts, setImageCounts] = useState({}); // batchId → count

  const fetchImageCounts = useCallback(async () => {
    try {
      const res = await api.get(`/images/user/${userId}`);
      // 构建 batchId → imageCount 映射
      const map = {};
      for (const g of (res.data.groups || [])) {
        for (const b of g.batches) map[b.batchId] = b.images.length;
      }
      setImageCounts(map);
    } catch {}
  }, [userId]);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/dispatch/user-data/${userId}`);
      setGroups(res.data.groups || []);
      setUserInfo(res.data.user);
      // 默认展开今天的分组
      setExpanded(prev => {
        const next = { ...prev };
        if (Object.keys(next).length === 0) next[today] = true;
        return next;
      });
    } catch (err) {
      showToast(err.response?.data?.error || '加载失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [userId, showToast, today]);

  useEffect(() => { if (userId) { fetchData(); fetchImageCounts(); } }, [fetchData, fetchImageCounts, userId]);

  // 数据变化时实时刷新（接量/完成/删除推送）
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event === 'data_refresh' || event === 'stats_refresh') fetchData();
    });
  }, [subscribe, fetchData]);

  const toggleExpand = (date) => setExpanded(prev => ({ ...prev, [date]: !prev[date] }));

  const toggleSelect = (date) => setSelected(prev => ({ ...prev, [date]: !prev[date] }));

  const toggleSelectAll = () => {
    const allSelected = groups.length > 0 && groups.every(g => selected[g.date]);
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(groups.map(g => [g.date, true])));
  };

  const selectedDates = groups.filter(g => selected[g.date]).map(g => g.date);
  const selectedCount = selectedDates.length;
  const allSelected = groups.length > 0 && groups.every(g => selected[g.date]);
  const totalRecords = groups.reduce((s, g) => s + g.total, 0);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await api.delete(`/dispatch/user-data/${userId}`, { data: { dates: selectedDates } });
      showToast(res.data.message, 'success');
      setShowDeleteModal(false);
      setSelected({});
      fetchData();          // 立即刷新数据列表
      fetchImageCounts();   // 同步刷新图片计数
    } catch (err) {
      showToast(err.response?.data?.error || '删除失败', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const dateLabel = (date) => {
    if (date === today) return `今天 ${date}`;
    return date;
  };

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}
        >
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Search size={16} /> 用户数据 {userInfo && `- ${userInfo.name || userInfo.phone}`}
        </span>
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AdminNotificationBell />
          <button
            onClick={() => navigate(`/admin/images/${userId}`)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--c-primary)', fontSize: 13, fontWeight: 600 }}
          >
            <Camera size={16} /> 图片
          </button>
        </div>
      </div>

      <div style={{ padding: '12px 16px' }}>
        {/* 汇总 + 全选 */}
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
              style={{ width: 16, height: 16, accentColor: 'var(--c-primary)' }} />
            全选
          </label>
          <span style={{ flex: 1, fontSize: 13, color: 'var(--c-text-secondary)', textAlign: 'right' }}>
            共 {groups.length} 天 · {totalRecords} 条
          </span>
          <button className="btn btn-danger btn-sm" disabled={selectedCount === 0 || deleting}
            onClick={() => setShowDeleteModal(true)}>
            <Trash2 size={13} style={{ verticalAlign: '-2px', marginRight: 3 }} />
            删除{selectedCount > 0 ? `（${selectedCount}天）` : ''}
          </button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : groups.length === 0 ? (
          <div className="card empty-state">
            <div className="empty-icon"><MailX size={40} color="var(--c-text-secondary)" strokeWidth={1.2} /></div>
            <p>暂无数据</p>
          </div>
        ) : (
          groups.map(g => {
            const isOpen = !!expanded[g.date];
            return (
              <div key={g.date} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {/* 日期分组头：勾选 + 日期 + 统计 + 折叠箭头 */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer', background: g.date === today ? 'var(--c-primary-light)' : 'transparent' }}
                  onClick={() => toggleExpand(g.date)}
                >
                  <input type="checkbox" checked={!!selected[g.date]}
                    onClick={e => e.stopPropagation()}
                    onChange={() => toggleSelect(g.date)}
                    style={{ width: 16, height: 16, accentColor: 'var(--c-primary)', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{dateLabel(g.date)}</div>
                    <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginTop: 1 }}>
                      {g.total} 条
                      {g.completed > 0 && <span style={{ color: 'var(--c-success)' }}> · 完成 {g.completed}</span>}
                      {g.active > 0 && <span style={{ color: 'var(--c-accent)' }}> · 进行中 {g.active}</span>}
                    </div>
                  </div>
                  {isOpen ? <ChevronDown size={18} color="var(--c-text-secondary)" /> : <ChevronRight size={18} color="var(--c-text-secondary)" />}
                </div>

                {/* 展开后的数据列表：#1 手机号 */}
                {isOpen && (
                  <div style={{ padding: '0 14px 8px' }}>
                    {/* 按批次分组展示 + 图片数标记 */}
                    {(() => {
                      // 同一日期可能有多个 batch_id（多批次），按批次分组后展示
                      const batchMap = new Map();
                      for (const r of g.records) {
                        if (!batchMap.has(r.batch_id)) batchMap.set(r.batch_id, []);
                        batchMap.get(r.batch_id).push(r);
                      }
                      const batches = [...batchMap.entries()];
                      return batches.map(([batchId, records], bIdx) => {
                        const imgCount = imageCounts[batchId] || 0;
                        return (
                          <div key={batchId} style={{ marginBottom: bIdx < batches.length - 1 ? 8 : 0 }}>
                            {/* 批次小标题 */}
                            {batches.length > 1 && (
                              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-secondary)', padding: '6px 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                第{bIdx + 1}批 · {records.length}条
                                {imgCount > 0 && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'rgba(52,199,89,0.1)', color: 'var(--c-success)', borderRadius: 6, padding: '2px 7px', fontSize: 11 }}>
                                    <Camera size={10} /> {imgCount}张
                                  </span>
                                )}
                                {imgCount === 0 && (
                                  <span style={{ fontSize: 11, color: 'var(--c-text-tertiary)' }}>无图片</span>
                                )}
                              </div>
                            )}
                            {batches.length === 1 && imgCount > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0 6px', fontSize: 12 }}>
                                <Camera size={12} color="var(--c-success)" />
                                <span style={{ color: 'var(--c-success)' }}>已上传 {imgCount} 张凭证</span>
                              </div>
                            )}
                            {batches.length === 1 && imgCount === 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0 6px', fontSize: 12, color: 'var(--c-text-tertiary)' }}>
                                <Camera size={12} /> 未上传图片
                              </div>
                            )}
                            {records.map((r, i) => (
                              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', borderBottom: i < records.length - 1 ? '0.5px solid var(--c-divider)' : 'none' }}>
                                <span style={{ fontSize: 12, color: 'var(--c-text-tertiary)', width: 22, flexShrink: 0 }}>#{i + 1}</span>
                                <span style={{ flex: 1, fontWeight: 600, fontSize: 15, color: 'var(--c-primary)', letterSpacing: '0.5px' }}>
                                  {r.customer_phone || r.customer_name || '—'}
                                </span>
                                <span className={`badge ${r.status === 'completed' ? 'badge-success' : 'badge-warning'}`} style={{ flexShrink: 0 }}>
                                  {r.status === 'completed' ? '已完成' : '进行中'}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* 批量删除确认弹窗 */}
      <Modal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="删除用户数据">
        <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
          <AlertTriangle size={44} color="var(--c-accent)" style={{ margin: '0 auto 12px', display: 'block' }} />
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
            删除所选 {selectedCount} 天的派单数据？
          </div>
          <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', marginBottom: 16, lineHeight: 1.8, textAlign: 'left', background: 'var(--c-bg)', borderRadius: 10, padding: '10px 14px', maxHeight: 140, overflowY: 'auto' }}>
            {selectedDates.map(d => <div key={d}>{dateLabel(d)}</div>)}
          </div>
          <p style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 16 }}>所选日期的派单记录和任务批次将被彻底删除，已生成的结算记录保留</p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-outline" style={{ flex: 1, height: 46 }} onClick={() => setShowDeleteModal(false)}>取消</button>
            <button className="btn btn-primary" style={{ flex: 1, height: 46 }} onClick={handleDelete} disabled={deleting}>
              {deleting ? '删除中...' : '确认删除'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
